/**
 * Local Wikipedia-style order-type lore articles.
 * Keeps the existing order-type titles; texts are Coming soon until authored.
 * Content is authored here — not loaded from the database.
 */
import { createComingSoonArticle } from '../createComingSoonArticle';

/** Existing lore order-type titles (from lore_entries / add_order_types migration). */
const ORDER_TYPE_TITLES = [
  'Stratagem',
  'Drone',
  'Weapon',
  'Exolot Juggernaut',
  'Undead',
  'Dinosaur',
  'Immortal',
  'Exolot Experiment',
  'Cyborg',
  'Combat Orb',
  'Human',
  'Caropaera',
  'Caropaera Rebel',
  'Infected',
  'Howler',
  'The Ennead',
  'Mutant',
  'Animal',
  'Plate',
  'Kendrex',
  'Sentient',
  'War Machine',
  'Battle Engine',
  'Shadowbound',
  'Exolot Last Generation',
  'Exolot',
];

export const DEFAULT_ORDER_TYPE_LORE_TEXT = 'Coming soon.';

/** @type {Record<string, { title: string, text: string, format: string }>} */
const orderTypeArticles = Object.fromEntries(
  ORDER_TYPE_TITLES.map((title) => [title, createComingSoonArticle({ title })])
);

/**
 * Merge API order-type rows with local Coming soon / authored texts.
 * Existing titles are preserved; old DB flavor text is never shown.
 * @param {Array} apiEntries
 * @returns {Array}
 */
export function mergeOrderTypeLoreArticles(apiEntries) {
  const entries = Array.isArray(apiEntries) ? [...apiEntries] : [];
  const byTitle = new Map();

  for (const entry of entries) {
    if (!entry || !entry.title) {
      throw new Error('mergeOrderTypeLoreArticles: entry.title is required');
    }
    byTitle.set(entry.title.toLowerCase(), entry);
  }

  for (const [title, article] of Object.entries(orderTypeArticles)) {
    const key = title.toLowerCase();
    const existing = byTitle.get(key);
    const merged = {
      id: existing?.id != null ? existing.id : `local-wiki-order-type-${article.title}`,
      title: article.title,
      text: article.text || DEFAULT_ORDER_TYPE_LORE_TEXT,
      format: article.format || 'wiki',
      ...(article.image || existing?.image
        ? { image: article.image || existing.image }
        : {}),
    };
    byTitle.set(key, merged);
  }

  // Any unexpected API-only order types: keep title, strip old text
  const knownTitles = new Set(ORDER_TYPE_TITLES.map((title) => title.toLowerCase()));
  for (const [key, entry] of byTitle.entries()) {
    if (!knownTitles.has(key)) {
      byTitle.set(key, {
        ...entry,
        text: DEFAULT_ORDER_TYPE_LORE_TEXT,
        format: 'wiki',
      });
    }
  }

  return Array.from(byTitle.values());
}

export default orderTypeArticles;
